import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/aleart-design";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const aleartDesign: CaseStudy = {
  slug: "aleart-design",
  title: "ALEART DESIGN",
  cardTitle: "Сайт-портфолио для студии дизайна интерьера ALEART DESIGN",
  subtitle: "Студия дизайна интерьера и архитектуры",
  industry: { label: "Строительство и архитектура", slug: "construction" },
  eyebrow: "Корпоративный сайт · Интерьеры и архитектура",
  h1: "Разработка сайта студии дизайна интерьера ALEART DESIGN",
  lead:
    "Разработали сайт студии дизайна интерьера ALEART DESIGN: портфолио реализованных проектов, услуги дизайна, ремонта и комплектации, расчёт проекта и формы связи с менеджером.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт дизайн-студии" },

  seo: {
    primaryKeyword: "разработка сайта студии дизайна интерьера",
    secondaryKeywords: [
      "сайт для дизайн-студии",
      "разработка сайта интерьерного бюро",
      "сайт архитектурной студии",
      "портфолио студии интерьера",
    ],
    semanticEntities: [
      "портфолио проектов",
      "дизайн-проект",
      "ремонт",
      "комплектация",
      "проект под ключ",
      "расчёт проекта",
    ],
    excludedKeywords: [
      "дизайн интерьера заказать",
      "ремонт квартиры",
      "дизайн-проект цена",
      "архитектор Москва",
    ],
    title: "Сайт ALEART DESIGN — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт ALEART DESIGN с портфолио, услугами дизайна и ремонта, комплектацией, расчётом проекта и международной географией.",
    h1: "Разработка сайта студии дизайна интерьера ALEART DESIGN",
    slug: "aleart-design",
    canonicalUrl: `${SITE_URL}/cases/aleart-design`,
    ogTitle: "Сайт студии дизайна интерьера ALEART DESIGN",
    ogDescription:
      "Портфолио проектов, услуги дизайна, ремонта и комплектации, расчёт проекта и формы связи.",
    ogImage: `${SITE_URL}${dir}/aleart-design-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Портфолио, услуги и заявки",
    },
    relatedIndustry: {
      label: "Сайты для архитектурных и дизайн-студий",
      to: routes.sites,
      note: "Портфолио проектов и расчёт стоимости",
    },
    relatedCases: [
      {
        label: "Сайт-портфолио архитектурного бюро Archidica",
        to: "/cases/archidica",
        note: "Портфолио проектов и заявки для частных и бизнес-клиентов",
      },
      {
        label: "Корпоративный сайт застройщика ESG",
        to: "/cases/esg",
        note: "Сайт компании из строительной отрасли",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить сайт дизайн-студии",
  },

  client: {
    name: "ALEART DESIGN",
    url: "https://aleart-design.ru/",
    city: "Москва",
    industry: "Интерьеры и архитектура",
  },
  year: "2026",
  services: [
    "Дизайн сайта студии",
    "Портфолио проектов",
    "Разделы услуг: дизайн, ремонт, комплектация, под ключ",
    "Раздел состава дизайн-проекта",
    "Форма расчёта проекта",
  ],
  platform: "—",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "ALEART DESIGN" },
    { label: "Отрасль", value: "Интерьеры и архитектура" },
    { label: "Тип проекта", value: "Сайт-портфолио студии дизайна интерьера" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Портфолио с крупными проектами: сетка и детальная страница проекта",
      "Разделы услуг «Дизайн», «Ремонт», «Комплектация» и «Под ключ»",
      "Раздел состава дизайн-проекта: коллажи, смета, 3D-визуализации, рабочие чертежи",
      "Форма расчёта проекта и связи с менеджером",
      "Международная география проектов: Россия, Казахстан, Узбекистан, США, ОАЭ",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "ALEART DESIGN — студия полного цикла: дизайн-проект, ремонт, комплектация и работа под ключ, с проектами в нескольких странах. Сайту нужно было показать портфолио как главный аргумент и при этом объяснить, что входит в конкретную услугу.",
        "Вторая часть задачи — довести посетителя от знакомства с портфолио до расчёта проекта, не перегружая его длинной анкетой на входе.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: editorial-подача вместо декоративных рамок",
    paragraphs: [
      "Первый экран собран в тёплой stone/ivory палитре с одним сильным фото проекта — акцент сделан на реализованной работе студии, а не на графических элементах интерфейса.",
      "Портфолио и разделы услуг выстроены как редакционная подача: крупные фотографии, минимум декора, чёткая сетка карточек.",
    ],
    bullets: [
      "Тёплая stone/ivory палитра",
      "Крупные фотографии реализованных проектов",
      "Минимум декоративных элементов интерфейса",
    ],
  },

  identity: {
    fonts: [
      { name: "TT Wellingtons", role: "Заголовки", sample: "Портфолио проектов" },
      { name: "Manrope", role: "Текст и навигация", sample: "Дизайн-проект" },
    ],
    colors: [
      { hex: "#F4EFE8", name: "Основной фон" },
      { hex: "#8C7A64", name: "Акцент бренда" },
      { hex: "#211E1A", name: "Текст" },
    ],
  },

  cover: img(
    "aleart-design-case-cover",
    "Главная страница сайта студии дизайна интерьера ALEART DESIGN с портфолио проектов",
  ),

  gallery: [
    img("aleart-design-portfolio", "Портфолио проектов на сайте ALEART DESIGN", 6),
    img("aleart-design-services", "Раздел услуг дизайна, ремонта и комплектации ALEART DESIGN", 3),
    img("aleart-design-project", "Состав дизайн-проекта и форма расчёта на сайте ALEART DESIGN", 3),
  ],

  responsive: {
    text: [
      "Портфолио и разделы услуг на мобильной версии идут горизонтальным скроллом крупных карточек — так же, как десктопная сетка 2×2, но без уменьшения коллажа.",
      "Форма расчёта проекта доступна с любого раздела мобильной версии за один переход.",
    ],
    images: [
      mobile("aleart-design-mobile-home", "Мобильная главная сайта ALEART DESIGN"),
      mobile("aleart-design-mobile-portfolio", "Мобильное портфолио проектов ALEART DESIGN"),
    ],
  },

  columnBlocks: [
    {
      id: "portfolio",
      title: "Портфолио",
      paragraphs: [
        "Крупные реализованные проекты представлены сеткой с переходом на детальную страницу — с описанием, фотографиями и составом работ по конкретному объекту.",
      ],
      bullets: ["Сетка крупных проектов", "Детальная страница проекта", "География проектов"],
    },
    {
      id: "services",
      title: "От проекта до реализации",
      paragraphs: [
        "Услуги «Дизайн», «Ремонт», «Комплектация» и «Под ключ» собраны в блок 2×2 на десктопе и горизонтальным скроллом на мобильном — клиент видит весь цикл работы студии на одном экране.",
      ],
      columns: [
        { title: "Дизайн", items: ["Дизайн-проект"] },
        { title: "Ремонт", items: ["Ремонтные работы"] },
        { title: "Комплектация", items: ["Подбор и закупка"] },
        { title: "Под ключ", items: ["Полный цикл реализации"] },
      ],
    },
    {
      id: "composition",
      title: "Состав дизайн-проекта",
      paragraphs: [
        "Раздел показывает дизайн-проект как конкретный продукт: коллажи, смета, 3D-визуализации и рабочие чертежи, а не абстрактное «сопровождение».",
      ],
      bullets: ["Коллажи и подбор материалов", "Смета проекта", "3D-визуализации", "Рабочие чертежи"],
    },
    {
      id: "calculation",
      title: "Расчёт проекта",
      paragraphs: [
        "Форма расчёта проекта и связь с менеджером доступны сразу после знакомства с портфолио и услугами — без промежуточных страниц.",
      ],
      bullets: ["Форма расчёта проекта", "Связь с менеджером"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "Портфолио и каталог услуг", to: routes.sites },
    ],
  },
};
